import { mfaService } from './mfa.service';
import { supabase } from './supabase';

/**
 * Payment OTP Service
 * Handles OTP verification for payment operations using Supabase TOTP
 * This is frontend-only validation - no OTP sent to backend
 */

export interface PaymentOtpVerificationResult {
  valid: boolean;
  error?: string;
}

export const paymentOtpService = {
  /**
   * Verify OTP code for payment authorization
   * Uses Supabase MFA TOTP verification
   */
  async verifyPaymentOtp(code: string): Promise<PaymentOtpVerificationResult> {
    try {
      // Get the user's MFA factors
      const factors = await mfaService.listFactors();

      if (factors.length === 0) {
        return {
          valid: false,
          error: 'No se encontró ningún factor MFA configurado. Configura MFA primero.',
        };
      }

      // Get the first verified factor
      const verifiedFactor = factors.find(f => f.status === 'verified');

      if (!verifiedFactor) {
        return {
          valid: false,
          error: 'No se encontró ningún factor MFA verificado. Completa la configuración de MFA.',
        };
      }

      // Create a challenge
      const challenge = await mfaService.createChallenge(verifiedFactor.id);

      // Verify the code against the challenge
      const { error } = await supabase.auth.mfa.verify({
        factorId: verifiedFactor.id,
        challengeId: challenge.id,
        code,
      });

      if (error) {
        return {
          valid: false,
          error: 'Código OTP incorrecto. Intenta nuevamente.',
        };
      }

      // If we get here, verification was successful
      return {
        valid: true,
      };
    } catch (error) {
      // Only log in development
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('Error en la verificación OTP de pago:', error);
      }
      return {
        valid: false,
        error: 'Ocurrió un error durante la verificación. Intenta nuevamente.',
      };
    }
  },

  /**
   * Check if user has MFA enabled
   */
  async hasMfaEnabled(): Promise<boolean> {
    try {
      const factors = await mfaService.listFactors();
      return factors.some(f => f.status === 'verified');
    } catch {
      return false;
    }
  },
};
